import { Link, router, usePage } from "@inertiajs/react"
import { ChevronRight, MapPin, Receipt, ShoppingBag } from "lucide-react"
import { ReactNode } from "react"
import { UserLayout } from "@/components/layouts/user-layout"
import { Button } from "@/components/ui/button"
import { ScreenHeader } from "@/components/ui/headers"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import { t } from "@/lib/i18n"

type ReceiptListItem = {
  id: number
  status: "open" | "closed" | "paid" | "cancelled"
  statusLabel: string
  locationName: string
  tableName: string | null
  serviceContext: "dine_in" | "pickup"
  currency: string
  total: string
  itemCount: number
  createdAt: string
  timeZone: string
}

type Pagination = {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
  previousPage: number | null
  nextPage: number | null
}

type Props = {
  receipts: ReceiptListItem[]
  pagination: Pagination
}

/**
 * The account history — one row per past bill, across restaurants.
 *
 * Distinct from /orders, which covers the visit happening right now and lists
 * order lines: one row per dish. Months of that reads as a wall of dishes with
 * no sense of when or where. A receipt is the unit someone actually remembers,
 * so the row is the visit and the detail is the receipt screen that exists.
 */
export default function UserReceiptsIndex() {
  const { receipts, pagination } = usePage<Props>().props

  return (
    <UserLayout>
      <div className="text-foreground" data-testid="receipts-index-view">
        <ScreenHeader title={t("ui.react.pages.user.receipts.index.title")} />

        {receipts.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <ul className="flex flex-col px-4 pb-2 pt-2" data-testid="receipts-list">
              {receipts.map((receipt) => (
                <ReceiptRow key={receipt.id} receipt={receipt} />
              ))}
            </ul>
            <Pager pagination={pagination} />
          </>
        )}
      </div>
    </UserLayout>
  )
}

function ReceiptRow({ receipt }: { receipt: ReceiptListItem }) {
  const settled = receipt.status === "paid"

  return (
    <li>
      <Link
        href={`/bills/${receipt.id}/receipt`}
        data-testid={`receipt-list-item-${receipt.id}`}
        className="flex min-h-11 items-center gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-muted/50"
      >
        <div className="min-w-0 flex-1 space-y-1">
          <p className="truncate text-sm font-medium">{receipt.locationName}</p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {/* The restaurant's zone, not the browser's: someone reading this on
                a trip should see the hour they actually ate at. */}
            <span className="tabular-nums">
              {formatDateTime(receipt.createdAt, { timeZone: receipt.timeZone })}
            </span>
            <MetaChip>
              {receipt.serviceContext === "dine_in" ? (
                <>
                  <MapPin className="size-3" />
                  {receipt.tableName ?? t("ui.react.pages.user.receipts.index.dine_in")}
                </>
              ) : (
                <>
                  <ShoppingBag className="size-3" />
                  {t("ui.react.pages.user.receipts.index.pickup")}
                </>
              )}
            </MetaChip>
            {/* Colour lives in a small mark, never in the figure. An unsettled
                bill is the only state worth marking here. */}
            {!settled && (
              <MetaChip>
                <span className="size-2 rounded-full bg-warning" aria-hidden="true" />
                {receipt.statusLabel}
              </MetaChip>
            )}
          </div>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-sm font-semibold tabular-nums">
            {formatMoney(receipt.total, { currency: receipt.currency })}
          </p>
          <p className="text-xs tabular-nums text-muted-foreground">
            {t("ui.react.pages.user.receipts.index.items", { count: receipt.itemCount })}
          </p>
        </div>

        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      </Link>
    </li>
  )
}

function MetaChip({ children }: { children: ReactNode }) {
  return <span className="inline-flex items-center gap-1">{children}</span>
}

function Pager({ pagination }: { pagination: Pagination }) {
  if (pagination.totalPages <= 1) return null

  const go = (page: number | null) => {
    if (page === null) return
    router.get("/receipts", { page }, { preserveScroll: true })
  }

  return (
    <div className="flex items-center justify-between gap-3 px-4 pb-6" data-testid="receipts-pager">
      <Button
        variant="outline"
        size="sm"
        disabled={!pagination.hasPrevious}
        onClick={() => go(pagination.previousPage)}
        data-testid="receipts-prev-page"
      >
        {t("ui.react.pages.user.receipts.index.previous")}
      </Button>
      <span className="text-xs tabular-nums text-muted-foreground">
        {t("ui.react.pages.user.receipts.index.page_of", {
          page: pagination.page,
          total: pagination.totalPages,
        })}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={!pagination.hasNext}
        onClick={() => go(pagination.nextPage)}
        data-testid="receipts-next-page"
      >
        {t("ui.react.pages.user.receipts.index.next")}
      </Button>
    </div>
  )
}

function EmptyState() {
  return (
    <div
      data-testid="receipts-list-empty"
      className="flex flex-col items-center gap-3 px-6 py-16 text-center text-muted-foreground"
    >
      <span className="flex size-14 items-center justify-center rounded-full bg-muted">
        <Receipt className="size-6" />
      </span>
      <p className="text-sm font-medium">{t("ui.react.pages.user.receipts.index.empty_title")}</p>
      <p className="max-w-64 text-xs">{t("ui.react.pages.user.receipts.index.empty_hint")}</p>
    </div>
  )
}
